import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth, isJoinInProgress } from '@/lib/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { knoxApi, type ListOAuthProvidersResponse } from '@/lib/knox-api';
import { useBrand, useLogoUrl } from '@/lib/brand';
import { isWechatBrowser, isWechatAutoLoginSuppressed } from '@/lib/utils/wechat-browser';
import { isFeishuClient, isFeishuAutoLoginSuppressed } from '@/lib/utils/feishu-browser';
import { OAuthButtons } from './OAuthButtons';
import { EmailLoginForm } from './EmailLoginForm';
import { FeishuAutoLogin } from './FeishuAutoLogin';
import { AuthFooter } from './AuthFooter';

export function LoginPage() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('knox-aiops');
  const brand = useBrand();
  const logoUrl = useLogoUrl();
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [emailStep, setEmailStep] = useState<string>('email');

  // 飞书客户端内 JSAPI 免登：仅飞书环境且未抑制时才查 providers 取 app_id。
  // 抑制机制见 feishu-browser.ts（登出后避免自动免登死循环、保留切其他登录方式）。
  const inFeishu = isFeishuClient() && !isFeishuAutoLoginSuppressed();
  const { data: providersData } = useQuery<ListOAuthProvidersResponse>({
    queryKey: ['oauthProviders', 'feishu-jsapi'],
    queryFn: () => knoxApi.listOAuthProviders(),
    enabled: inFeishu && !loading && !user,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const feishuAppId =
    providersData?.providers?.find((p) => p.provider === 'feishu')?.app_key ?? '';
  // 飞书免登触发条件：飞书客户端内 + 未登录 + 未抑制 + 已拿到 app_id。
  const showFeishuAutoLogin = inFeishu && !loading && !user && !!feishuAppId;

  // Read URL params once on mount
  const [urlTokens] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      inviteToken: params.get('invite_token'),
      joinToken: params.get('join_token'),
      error: params.get('error'),
    };
  });

  // Auto-redirect when already logged in with a workspace
  useEffect(() => {
    if (!loading && user?.default_workspace_id) {
      const params = new URLSearchParams(window.location.search);
      const redirect = params.get('redirect');
      const target = redirect && redirect.startsWith('/') && !redirect.startsWith('//')
        ? redirect
        : `/workspaces/${user.default_workspace_id}`;
      const openInbox = !redirect && (location.state as { openInbox?: boolean } | null)?.openInbox === true;
      navigate(target, {
        replace: true,
        ...(openInbox ? { state: { openInbox: true } } : {}),
      });
    }
  }, [user, loading, location.state, navigate]);

  // WeChat H5 auto-redirect: when opened in WeChat in-app browser and not yet
  // logged in, skip the login form and go straight to WeChat OAuth authorization.
  // Preserves invite/join tokens in the redirect_uri so they survive the OAuth
  // round-trip via the backend state store (oauth_state.go RedirectURL).
  //
  // 抑制：用户主动登出后（logout 写入 sessionStorage 标记），不再自动跳转，
  // 否则微信侧保留的公众号授权会静默重新登录，造成"退出后又自动登录"死循环。
  // 用户主动点微信登录按钮时会清除该标记。
  useEffect(() => {
    if (loading || user || !isWechatBrowser()) return;
    if (isWechatAutoLoginSuppressed()) return;

    const search = window.location.search; // preserves ?invite_token=ABC&join_token=XYZ
    const redirectUri = `/login${search || ''}`;
    window.location.href = knoxApi.oauthAuthorizeUrl('wechat_h5', redirectUri);
  }, [loading, user]);

  // Not logged in but has invite/join token → save for later auto-fill on org-binding.
  // Clear the opposite token so a stale token from a previous visit can't hijack the current intent.
  useEffect(() => {
    if (urlTokens.inviteToken) {
      localStorage.removeItem('pending_join_token');
      localStorage.setItem('pending_invite_token', urlTokens.inviteToken);
    }
    if (urlTokens.joinToken) {
      localStorage.removeItem('pending_invite_token');
      localStorage.setItem('pending_join_token', urlTokens.joinToken);
    }
  }, [urlTokens]);

  // Logged in but no org → auto-join or redirect to org-binding
  // Guard: only run when user has neither org_id nor default_workspace_id.
  // Users WITH an org are handled by the login/register callbacks in auth.tsx.
  useEffect(() => {
    if (loading || !user || user.org_id || user.default_workspace_id) return;

    const joinToken = localStorage.getItem('pending_join_token');
    if (joinToken) {
      localStorage.removeItem('pending_join_token');
      localStorage.removeItem('pending_invite_token');
      knoxApi.joinOrgLoggedIn({ token: joinToken })
        .then(() => { window.location.href = '/workspaces'; })
        .catch(() => navigate('/org-binding', { replace: true }));
      return;
    }

    const inviteToken = localStorage.getItem('pending_invite_token');
    localStorage.removeItem('pending_invite_token');
    if (inviteToken) {
      navigate(`/invite`, { state: { token: inviteToken }, replace: true });
      return;
    }

    // Don't navigate while consumeJoinToken is in-flight (register callback is handling it)
    if (isJoinInProgress()) return;

    navigate('/org-binding', { replace: true });
  }, [user, loading, navigate]);

  // Read OAuth error from URL
  useEffect(() => {
    if (urlTokens.error) {
      setOauthError(urlTokens.error);
      window.history.replaceState({}, '', '/login');
    }
  }, [urlTokens.error]);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (user) return null;

  return (
    <div className="flex h-screen flex-col bg-white font-[Inter,sans-serif] text-foreground">
      {/* Header */}
      <header className="flex shrink-0 w-full items-center justify-between px-6 py-3 bg-white/80 backdrop-blur-md border-b border-black/[0.04]">
        <div className="flex items-center gap-2">
          <img src={logoUrl} alt={brand} className="h-7 w-auto" />
          <span className="font-heading text-xl font-extrabold tracking-tighter text-foreground">{brand}</span>
        </div>
      </header>

      {/* Main */}
      <main className="relative flex flex-1 min-h-0 flex-col items-center justify-center overflow-auto px-4 py-6">
        <div className="knox-dot-grid pointer-events-none absolute inset-0 opacity-10" />
        <div className="knox-card-glass relative w-full max-w-[420px] p-6 sm:p-8">
          {/* Logo + Heading — logo always visible, title changes by step */}
          <div className={`${emailStep === 'verify-email' ? 'mb-4' : 'mb-6 sm:mb-8'} text-center`}>
            <div className="mb-3 flex justify-center">
              <img src={logoUrl} alt="" className="h-10 sm:h-12 w-auto" />
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-foreground mb-1">
              {emailStep === 'verify-email' ? t('loginForm.verifyEmailTitle')
                : emailStep === 'set-password' ? t('loginForm.setPasswordTitle')
                : emailStep === 'register' ? t('loginForm.registerTitle')
                : t('login.signIn')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {emailStep === 'set-password' ? t('loginForm.setPasswordSubtitle')
                : emailStep === 'verify-email' ? t('loginForm.verifyEmailSubtitle')
                : emailStep === 'register' ? t('loginForm.registerSubtitle')
                : t('login.subtitle')}
            </p>
          </div>

          {/* OAuth error banner */}
          {oauthError && (
            <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3">
              <p className="text-sm text-destructive">
                {t(`login.oauthError.${oauthError}`, t('login.oauthError.unknown'))}
              </p>
            </div>
          )}

          {/* 飞书客户端内 JSAPI 免登：替代普通登录选择区，自动免登 */}
          {showFeishuAutoLogin ? (
            <FeishuAutoLogin appId={feishuAppId} />
          ) : (
            <>
          {/* OAuth buttons — only on email step (user hasn't chosen a login method yet) */}
          {emailStep === 'email' && (
            <>
              <div className="mb-6 sm:mb-8">
                <OAuthButtons inviteToken={urlTokens.inviteToken} joinToken={urlTokens.joinToken} />
              </div>

              {/* Divider */}
              <div className="mb-6 sm:mb-8 flex items-center">
                <div className="flex-1 border-t border-black/[0.16]" />
                <span className="mx-4 text-xs uppercase tracking-widest font-medium text-muted-foreground/40">{t('login.or')}</span>
                <div className="flex-1 border-t border-black/[0.16]" />
              </div>
            </>
          )}

          {/* Email login form */}
          <EmailLoginForm onStepChange={setEmailStep} />
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <AuthFooter />
    </div>
  );
}
